import type { App } from "vue";
import type { Router } from "vue-router";

import type { useRuntimeErrorStore } from "@/stores/runtimeError";

type RuntimeErrorStore = ReturnType<typeof useRuntimeErrorStore>;
type VueErrorHandler = NonNullable<App["config"]["errorHandler"]>;

interface InstallationRecord {
  dispose: () => void;
  originalVueHandler: VueErrorHandler | undefined;
}

const installations = new WeakMap<App, InstallationRecord>();

export function installGlobalErrorHandlers(
  app: App,
  router: Router,
  store: RuntimeErrorStore,
  target: Window = window,
): () => void {
  const previousInstallation = installations.get(app);
  const originalVueHandler =
    previousInstallation === undefined
      ? app.config.errorHandler
      : previousInstallation.originalVueHandler;
  previousInstallation?.dispose();

  const installedVueHandler: VueErrorHandler = (caught) =>
    store.capture("render", caught, ["retry-view", "go-home", "reload"]);
  app.config.errorHandler = installedVueHandler;

  const removeRouterHandler = router.onError((caught) =>
    store.capture("navigation", caught, ["go-home", "reload"]),
  );

  const rejectionHandler = (event: PromiseRejectionEvent): void => {
    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();
    store.capture("unhandled-rejection", event.reason, ["reload", "go-home"]);
  };
  target.addEventListener("unhandledrejection", rejectionHandler);

  let disposed = false;
  const record: InstallationRecord = {
    originalVueHandler,
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;

      target.removeEventListener("unhandledrejection", rejectionHandler);
      removeRouterHandler();

      if (app.config.errorHandler === installedVueHandler) {
        if (originalVueHandler === undefined) {
          delete app.config.errorHandler;
        } else {
          app.config.errorHandler = originalVueHandler;
        }
      }
      if (installations.get(app) === record) {
        installations.delete(app);
      }
    },
  };

  installations.set(app, record);
  return record.dispose;
}
